import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Textarea from '@mui/joy/Textarea';
import Sheet from '@mui/joy/Sheet';
import Avatar from '@mui/joy/Avatar';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import { PaperPlaneTilt as SendRounded, Headset as SupportAgentOutlined, Robot as SmartToyOutlined } from '@phosphor-icons/react';
import type { AIChatMessage } from '../../types/support';
import { useAIChat } from '../../hooks/useSupport';

interface AIChatPanelProps {
  messages: AIChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIChatMessage[]>>;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  onEscalate?: (context: AIChatMessage[]) => void;
  compact?: boolean;
}

export default function AIChatPanel({
  messages,
  setMessages,
  conversationId,
  setConversationId,
  onEscalate,
  compact = false,
}: AIChatPanelProps) {
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const aiChat = useAIChat();

  useEffect(() => {
    // Scroll the message list itself (not the page) — scrollIntoView() bubbles to
    // the page scroller and was yanking the whole support page to the bottom on load.
    const c = containerRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || aiChat.isPending) return;

    const userMessage: AIChatMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');

    try {
      const result = await aiChat.mutateAsync({
        message: text,
        history: newMessages.slice(1), // exclude welcome message from API call
        conversationId,
      });
      setMessages(prev => [...prev, { role: 'assistant' as const, content: result.response }]);
      if (result.conversation_id) {
        setConversationId(result.conversation_id);
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant' as const, content: "I'm having trouble right now. Please try again or submit a support ticket." },
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack sx={{ height: '100%', minHeight: compact ? 0 : 400 }}>
      {/* Chat messages */}
      <Box ref={containerRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.map((msg, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'flex-start', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}
          >
            {msg.role === 'assistant' && (
              <Avatar size="sm" sx={{ bgcolor: '#7013f5', flexShrink: 0 }}>
                <SmartToyOutlined size={16} />
              </Avatar>
            )}
            <Sheet
              variant={msg.role === 'user' ? 'solid' : 'soft'}
              color={msg.role === 'user' ? 'primary' : 'neutral'}
              sx={{
                p: 1.5,
                borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                maxWidth: '85%',
              }}
            >
              {msg.role === 'user' ? (
                <Typography
                  level="body-sm"
                  sx={{ whiteSpace: 'pre-wrap', color: 'white' }}
                >
                  {msg.content}
                </Typography>
              ) : (
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    '& p': { m: 0, mb: 0.75 },
                    '& p:last-child': { mb: 0 },
                    '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
                    '& li': { mb: 0.25 },
                    '& strong': { fontWeight: 700 },
                    '& h1, & h2, & h3': { fontSize: '0.95rem', fontWeight: 700, mt: 1, mb: 0.5 },
                    '& code': {
                      fontSize: '0.8rem',
                      bgcolor: 'background.level2',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 'xs',
                    },
                  }}
                >
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </Box>
              )}
            </Sheet>
          </Stack>
        ))}
        {aiChat.isPending && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar size="sm" sx={{ bgcolor: '#7013f5', flexShrink: 0 }}>
              <SmartToyOutlined size={16} />
            </Avatar>
            <Sheet variant="soft" color="neutral" sx={{ p: 1.5, borderRadius: '12px 12px 12px 4px' }}>
              <CircularProgress size="sm" />
            </Sheet>
          </Stack>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Escalate button */}
      {onEscalate && messages.length > 1 && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<SupportAgentOutlined />}
            onClick={() => onEscalate(messages)}
            sx={{ width: '100%' }}
          >
            Still need help? Submit a ticket
          </Button>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Textarea
            placeholder="Ask anything about ApexOS…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            minRows={1}
            maxRows={4}
            sx={{ flex: 1, fontSize: 'sm' }}
          />
          <IconButton
            variant="solid"
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || aiChat.isPending}
            sx={{ borderRadius: '50%', flexShrink: 0 }}
          >
            <SendRounded />
          </IconButton>
        </Stack>
      </Box>
    </Stack>
  );
}
